/**
 * Plan Transformers
 *
 * Transforms backend API responses to frontend domain models.
 * Centralizes all API → Domain conversions for academic plans.
 */

import type { EnrolledCourse } from '../../types/course';
import type { SemestersMap } from '../../types/plan';

/**
 * Backend API response shape for a single semester's data.
 * The backend returns courses_by_semester as a Record with this structure.
 */
export interface ApiSemesterData {
  courses: ApiCourse[];
  credits?: number;
  semester_name?: string;
  semester_code?: string;
  year?: number;
  season?: string;
}

/**
 * Grade data from backend (PlanetTerp/Testudo).
 */
export interface ApiGradeData {
  average_gpa?: number | null;
  difficulty?: 'easy' | 'moderate' | 'challenging' | 'hard' | null;
  total_students?: number | null;
  distribution?: {
    A?: number;
    B?: number;
    C?: number;
    D?: number;
    F?: number;
    W?: number;
  } | null;
}

/**
 * Backend API course shape.
 * Field names may differ from frontend (course_code vs code, etc.)
 */
export interface ApiCourse {
  code?: string;
  course_code?: string;
  title?: string;
  course_title?: string;
  credits?: number | null;
  status?: string;
  grade?: string | null;
  prerequisites?: unknown[];
  gen_ed_requirements?: string[];
  isPlaceholder?: boolean;
  placeholder_category?: string | null;
  // Grade/difficulty data from PlanetTerp
  grade_data?: ApiGradeData | null;
  // Course description
  description?: string;
  // Additional fields that may be present
  [key: string]: unknown;
}

/**
 * Backend API response shape for plan data.
 * Supports multiple response formats from different endpoints.
 */
export interface ApiPlanResponse {
  // Primary format: courses organized by semester code
  courses_by_semester?: Record<string, ApiSemesterData>;
  // Alternative format: direct semesters object (already transformed)
  semesters?: Record<string, ApiCourse[]> | ApiSemesterData[];
  // Plan metadata
  plan_id?: number;
  plan_name?: string;
  majors?: unknown[];
  minors?: unknown[];
  // Other fields we pass through
  [key: string]: unknown;
}

const optionalText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

/**
 * Transform a single API course to a domain EnrolledCourse.
 *
 * Normalizes field names and provides sensible defaults for missing data.
 */
export function toDomainCourse(apiCourse: ApiCourse | null | undefined): EnrolledCourse | null {
  if (!apiCourse) return null;

  return {
    code: apiCourse.code || apiCourse.course_code || 'UNKNOWN',
    title: apiCourse.title || apiCourse.course_title || 'Untitled Course',
    course_title: apiCourse.title || apiCourse.course_title || 'Untitled Course',
    credits: apiCourse.credits ?? 0,
    status: (apiCourse.status as EnrolledCourse['status']) || 'planned',
    // W43 P3 (#275): one grade column end-to-end; the client-side
    // earned_grade/grade COALESCE is gone with the second column.
    grade: apiCourse.grade ?? null,
    isPlaceholder: apiCourse.isPlaceholder || false,
    placeholder_category: apiCourse.placeholder_category ?? null,
    // Placeholder attributes (#205): persisted server-side in
    // placeholder_data and echoed back in the plan payload — without this
    // passthrough a reload would keep the placeholder but lose its declared
    // shape (type, requirements, notes, color).
    ...(apiCourse.isPlaceholder
      ? {
          placeholderType: apiCourse.placeholderType as string | undefined,
          requirements: apiCourse.requirements as EnrolledCourse['requirements'],
          notes: apiCourse.notes as string | undefined,
          color: apiCourse.color as string | undefined,
          createdAt: apiCourse.createdAt as string | undefined,
          updatedAt: apiCourse.updatedAt as string | undefined,
        }
      : {}),
    gen_ed_requirements: Array.isArray(apiCourse.gen_ed_requirements)
      ? apiCourse.gen_ed_requirements
      : [],
    // Pass through grade data for difficulty display
    grade_data: apiCourse.grade_data ?? null,
    // Pass through description for expanded view
    description: apiCourse.description,
    // Catalog requisite text for the expanded view
    prerequisites_raw: optionalText(apiCourse.prerequisites_raw),
    corequisites_raw: optionalText(apiCourse.corequisites_raw),
    restriction: optionalText(apiCourse.restriction),
    // Optimizer suggestion fields (from backend merger when include_suggestions=true)
    isSuggestion: apiCourse.is_suggestion === true,
    suggestionSource: apiCourse.suggestion_source as EnrolledCourse['suggestionSource'],
    requirementKey: (apiCourse.requirement_key as string | null) ?? null,
    confidence: typeof apiCourse.confidence === 'number' ? apiCourse.confidence as number : undefined,
    genEdArea: (apiCourse.gen_ed_area as string | null) ?? null,
    genEdAlternatives: Array.isArray(apiCourse.gen_ed_alternatives)
      ? apiCourse.gen_ed_alternatives as string[]
      : [],
    // Recorded causal reason for the suggestion (Phase 6b)
    reason: (apiCourse.reason as string | null) ?? null,
  };
}

/**
 * Transform an array of API courses, filtering out nulls.
 */
function toDomainCourses(apiCourses: (ApiCourse | null | undefined)[]): EnrolledCourse[] {
  return apiCourses
    .map(toDomainCourse)
    .filter((course): course is EnrolledCourse => course !== null);
}

/**
 * Transform backend API response to frontend SemestersMap.
 *
 * Handles two backend response formats:
 * 1. `courses_by_semester`: { "202408": { courses: [...], credits: 16 } }
 * 2. `semesters`: { "202408": [...] } (direct array format)
 *
 * @param backendData - Raw API response data
 * @returns SemestersMap with semester codes as keys and course arrays as values
 */
export function toDomainSemesters(backendData: ApiPlanResponse): SemestersMap {
  // Format 1: courses_by_semester (primary backend format)
  if (backendData.courses_by_semester && typeof backendData.courses_by_semester === 'object') {
    const semesters: SemestersMap = {};

    for (const [semesterCode, semesterData] of Object.entries(backendData.courses_by_semester)) {
      if (semesterData?.courses && Array.isArray(semesterData.courses)) {
        semesters[semesterCode] = toDomainCourses(semesterData.courses);
      } else {
        semesters[semesterCode] = [];
      }
    }

    return semesters;
  }

  // Format 2: Direct semesters object (fallback)
  const fallbackSemesters = backendData.semesters;
  if (!fallbackSemesters || typeof fallbackSemesters !== 'object') {
    return {};
  }

  // Check if it's an array (shouldn't be, but handle gracefully)
  if (Array.isArray(fallbackSemesters)) {
    return {};
  }

  const semesters: SemestersMap = {};

  for (const [semesterCode, courses] of Object.entries(fallbackSemesters)) {
    if (Array.isArray(courses)) {
      semesters[semesterCode] = toDomainCourses(courses as ApiCourse[]);
    } else {
      semesters[semesterCode] = [];
    }
  }

  return semesters;
}

